/**
 * Şeffaflık (P35, 21 Ekim 2026): /kurumsal "Nasıl Çalışıyoruz" bölümü, /fiyatlar "Çalışma Koşulları Özeti" ve sekiz
 * hizmet sayfasındaki kısa cevapların görünür güncelleme tarihi. Tek kaynak: About.tsx, Prices.tsx, AjansLanding.tsx,
 * PlatformHub.tsx ve SSR (server/seo/render.ts) bu modülü okur; metin bileşenlerde ayrıca yazılmaz.
 *
 * Saf TS ("@/" takma adı yok) ki render.ts de içe aktarabilsin. Rakamlar paketler.ts'ten üretilir, elle yazılmaz.
 * `kaynaklar` render edilmez (sss_data.ts deseni): her olgunun sitede/yasal metinde dayandığı yer.
 *
 * Bilerek yazılmayanlar (karar bekleyen ya da bu turda geçilen konular): hesap mülkiyeti istisnaları ve fesihte devir
 * süresi, erişimin nasıl verildiği, kreatif fikri mülkiyeti ve kreatif dosya teslimi, performans primi, ödeme vadesi,
 * indirimler, pazaryeri bağlantılarının kurulum kapsamı, minimum reklam bütçesi ve kreatif adetleri.
 */
import type { MetinParcasi } from "./icerik_tipleri";
import { PAKETLER, KURULUM_FIYAT, KURULUM_TESLIM_SURESI, paketAdi } from "./paketler";

/** /kurumsal bölümü ve /fiyatlar özetinin görünür güncelleme tarihi ("21 Ekim 2026" biçimi; ISO karşılığı isoTarih ile). */
export const CALISMA_GUNCELLEME = "21 Ekim 2026";
/** Sekiz hizmet sayfasındaki kısa cevap bloklarının görünür güncelleme tarihi (bölümden ayrı güncellenebilir). */
export const KISA_CEVAP_GUNCELLEME = "21 Ekim 2026";
/** Görünür tarih satırının etiketi (React ve SSR aynı metni basar). */
export const GUNCELLEME_ETIKETI = "Güncelleme:";

export const CALISMA_CAPA = "nasil-calisiyoruz";
export const CALISMA_YOLU = `/kurumsal#${CALISMA_CAPA}`;
export const CALISMA_BASLIK = "Nasıl Çalışıyoruz: Hesaplar, Sözleşme, Ücret ve Raporlama";

export interface CalismaMaddesi {
  id: string;
  /** H3 başlık (soru biçiminde değil; FAQPage'e girmez) */
  baslik: string;
  /** Paragraflar; her paragraf düz metin + cümle içi bağlantı parçaları */
  paragraflar: MetinParcasi[][];
  /** İsteğe bağlı ilgili soru-cevap bağlantısı (paragraflardan sonra ayrı satır) */
  baglanti?: { href: string; etiket: string };
  /** Olguların kaynağı (render edilmez) */
  kaynaklar: string[];
}

const tl = (n: number) => n.toLocaleString("tr-TR");
const paketFiyatlari = PAKETLER.map(p => `${paketAdi(p)} için ${tl(p.fiyat)} TL`).join(", ");

const HIZMET_SOZLESMESI = "/hizmet-sozlesmesi";
const IPTAL_IADE = "/iptal-ve-iade-politikasi";

export const CALISMA_GIRIS: MetinParcasi[] = [
  "Bu bölüm reklam yönetimi paketlerinde iş birliğinin temel kurallarını özetler. Bağlayıcı koşullar ",
  { href: HIZMET_SOZLESMESI, etiket: "Hizmet Sözleşmesi'nde" },
  ", size iletilen hizmet teklifinde ve ",
  { href: IPTAL_IADE, etiket: "İptal ve İade Politikası'nda" },
  " yer alır.",
];

export const CALISMA_MADDELERI: CalismaMaddesi[] = [
  {
    id: "hesaplar",
    baslik: "Reklam hesapları ve veri mülkiyeti",
    paragraflar: [
      [
        "Reklam yönetiminde varlıkları markanın kendi adına kurarız: Meta Business Manager ve reklam hesabı, Pixel, ürün kataloğu, Google Ads ve Merchant Center hesabı markanın adına açılır; hesaplarınız zaten varsa yönetici yetkisiyle dahil oluruz. Pixel ve dönüşüm verileri sözleşme gereği sizin mülkiyetinizdedir; iş birliği sona erdiğinde ajans yetkisi hesaplardan çıkarılır.",
      ],
    ],
    baglanti: { href: "/sss#ajans-adina-hesap", etiket: "Business Manager ve Pixel ajansın adına açılırsa ne olur?" },
    // "Reklam yönetiminde" kapsamı bilinçli: hesap kiralama hizmeti ve sözleşmedeki ajans Business Manager'ı hükmü bu
    // cümlenin dışında kalır (hesap mülkiyeti istisnaları bu turda yazılmaz).
    kaynaklar: ["client/src/lib/sss_data.ts", "client/src/pages/legal/HizmetSozlesmesi.tsx", "/sss", "/hizmet-sozlesmesi", "/e-ticaret-reklam-ajansi"],
  },
  {
    id: "sozlesme",
    baslik: "Sözleşme süresi ve fesih",
    paragraflar: [
      [
        "Aylık reklam yönetimi paketleri en az 3 aylık taahhütle başlar, ardından aylık dönemlerle kendiliğinden uzar. Çalışmayı sona erdirmek isteyen taraf, dönem sonundan en az 30 gün önceden yazılı bildirim yapar; e-postayla yapılan bildirim de geçerlidir. Marka tarafındaki iptalin geçerli olması için o tarihe kadar tahakkuk etmiş borçların ödenmiş olması gerekir.",
      ],
      [
        "Asgari süre dolmadan haklı bir neden olmaksızın ayrılan marka, sürenin kalan aylarına ait hizmet bedelinin tamamını öder. Ayrıntılar ",
        { href: HIZMET_SOZLESMESI, etiket: "Hizmet Sözleşmesi'nin 3. ve 8. maddelerinde" },
        " ve ",
        { href: IPTAL_IADE, etiket: "İptal ve İade Politikası'nda" },
        " yer alır.",
      ],
    ],
    // K1 (20 Ekim) ve K5 (21 Ekim): HizmetSozlesmesi.tsx madde 3, 8.1, 8.3; IptalIade.tsx madde 2 (yazılı, 30 gün,
    // tahakkuk etmiş borçlar) ve madde 4 (asgari süre, kalan ayların bedeli).
    kaynaklar: ["client/src/pages/legal/HizmetSozlesmesi.tsx", "client/src/pages/legal/IptalIade.tsx", "client/src/lib/paketler.ts", "/hizmet-sozlesmesi", "/iptal-ve-iade-politikasi"],
  },
  {
    id: "ucret",
    baslik: "Ücret modeli",
    paragraflar: [
      [
        `Reklam yönetimi sabit aylık paket ücretiyle çalışır ve paket ücretleri KDV hariçtir: ${paketFiyatlari}. Ücret reklam bütçesinden bağımsızdır ve bütçe üzerinden komisyon alınmaz; bütçeyi marka kendi kartıyla Meta, Google veya TikTok hesabına öder.`,
      ],
      [
        `Tek seferlik mağaza kurulumunun ücreti ise ${tl(KURULUM_FIYAT)} TL + KDV'dir. Hizmet bedeli yılda bir kez güncellenebilir; yeni tutar, uygulanacağı fatura döneminden en az bir ay önce yazılı olarak bildirilir.`,
      ],
    ],
    kaynaklar: ["client/src/lib/paketler.ts", "client/src/pages/legal/HizmetSozlesmesi.tsx", "client/src/lib/ek_icerik/statik_a.ts", "/fiyatlar", "/hizmet-sozlesmesi"],
  },
  {
    id: "raporlama",
    baslik: "Raporlama: metrikler ve sıklık",
    paragraflar: [
      [
        "Performans raporunu her pakette ayda bir paylaşırız; Profesyonel Full'de rapor kârlılık analizini de kapsar. Raporlarda reklam platformlarının rakamlarını mağazanın sipariş verisiyle karşılaştırırız; ROAS'ın yanında toplam pazarlama verimliliği (MER), müşteri edinme maliyeti (CAC) ve yeni müşteri oranı yer alır.",
      ],
    ],
    // K8 (21 Ekim): tüm paketlerde ayda bir rapor; Profesyonel Full "Aylık ROAS ve kârlılık raporu" (paketler.ts).
    kaynaklar: ["client/src/lib/paketler.ts", "client/src/lib/sss_data.ts", "client/src/lib/hizmet_detay/reklam_yonetimi.ts", "/fiyatlar", "/sss", "/hizmetlerimiz/reklam-yonetimi"],
  },
  {
    id: "erisim",
    baslik: "Veri erişimi ve gizlilik",
    paragraflar: [
      [
        "Çalışma boyunca reklam hesaplarınızı, GA4 mülkünüzü ve canlı rapor panelini kendiniz de doğrudan görüntüleyebilirsiniz. Ticari ve mali bilgileriniz sözleşme süresince ve sona ermesinden sonra iki yıl gizli tutulur; kişisel veriler KVKK kapsamında yalnızca sözleşme amaçlarıyla işlenir.",
      ],
    ],
    kaynaklar: ["client/src/lib/sss_data.ts", "client/src/lib/hizmet_detay/reklam_yonetimi.ts", "client/src/pages/legal/HizmetSozlesmesi.tsx", "/sss", "/hizmet-sozlesmesi"],
  },
  {
    id: "olcum",
    baslik: "Ölçüm kurulumu ve teslim",
    paragraflar: [
      [
        "Çalışma ölçümlemeyle başlar: Meta Pixel ve Conversions API, GA4 e-ticaret ölçümlemesi ve ürün kataloğu her reklam yönetimi paketine dahildir ve markanın kendi hesaplarına kurulur. Pixel ve kitle verisi markanın hesaplarında biriktiği için iş birliği bitince bu verinin ayrıca aktarılması gerekmez.",
      ],
      [
        `Tek seferlik mağaza kurulumunun teslimi ortalama ${KURULUM_TESLIM_SURESI} sürer; teslimin ardından 30 gün boyunca teknik destek ücretsizdir.`,
      ],
    ],
    // K2 (2-4 hafta) ve K3 (30 gün ücretsiz destek): paketler.ts. Fesihte devir süresi bilerek yazılmadı.
    kaynaklar: ["client/src/lib/paketler.ts", "client/src/lib/sss_data.ts", "client/src/lib/ek_icerik/statik_a.ts", "/fiyatlar", "/sss"],
  },
];

/** /fiyatlar: tam metin /kurumsal'da; burada tarihli kısa yönlendirme (kopya değil). ROI hesaplayıcıdan sonra, SSS'ten önce. */
export const FIYAT_CALISMA_OZETI: { baslik: string; paragraflar: MetinParcasi[][] } = {
  baslik: "Çalışma Koşulları Özeti",
  paragraflar: [
    [
      "Reklam hesaplarının ve verinin kimde kaldığını, raporların hangi sıklıkta ve hangi metriklerle verildiğini, veri erişimini ve ölçüm kurulumunun nasıl teslim edildiğini Kurumsal sayfamızdaki ",
      { href: CALISMA_YOLU, etiket: "Nasıl Çalışıyoruz bölümünde" },
      " tarihli olarak topladık.",
    ],
    [
      "Paket ücretleri bu sayfada; sözleşme süresi, fesih ve erken ayrılma kuralları ise ",
      { href: HIZMET_SOZLESMESI, etiket: "Hizmet Sözleşmesi'nde" },
      " yer alır.",
    ],
  ],
};

/** Parçalı paragrafın düz metni (testler ve kelime sayımı için). */
export const parcaMetni = (l: MetinParcasi[]) => l.map(x => (typeof x === "string" ? x : x.etiket)).join("");
